import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { ErrorNormalizerService, NormalizedError } from '../services/error-normalizer.service';

@Component({
  selector: 'app-error-message',
  standalone: true,
  imports: [CommonModule, JsonPipe],
  providers: [ErrorNormalizerService],
  template: `
@if (normalized) {
  <section
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    class="tw-rounded-xl tw-border tw-border-red-200 tw-bg-red-50 tw-p-4 tw-text-red-900 tw-space-y-3"
  >
    <div class="tw-flex tw-items-start tw-gap-3">
      <!-- Icon -->
      <div class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg tw-bg-red-100 tw-shrink-0" aria-hidden="true">
        <svg class="tw-h-6 tw-w-6 tw-text-red-600" viewBox="0 0 24 24" fill="none">
          <path d="M12 8v5m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </div>

      <div class="tw-flex-1 tw-min-w-0">
        <h3 class="tw-text-base tw-font-semibold tw-text-red-800">
          {{ heading }}
        </h3>

        <!-- Main message -->
        <p class="tw-mt-1 tw-text-sm tw-text-red-900 tw-break-words">
          {{ normalized.message }}
        </p>

        <!-- Chips -->
        <div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
          @if (normalized.reason) {
            <span class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-red-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-red-700">
              {{ normalized.reason }}
            </span>
          }
          @if (normalized.status) {
            <span class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-white tw-border tw-border-red-200 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-red-700">
              HTTP {{ normalized.status }}{{ normalized.statusText ? (' · ' + normalized.statusText) : '' }}
            </span>
          }
        </div>

        <!-- Actions -->
        <div class="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-text-xs tw-font-semibold tw-px-3 tw-py-2 tw-shadow-sm tw-transition"
            (click)="onCopy()"
          >
            <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 9h9v12H9zM6 3h9v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ copied ? 'Copied!' : 'Copy error' }}
          </button>

          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-white tw-border tw-border-red-200 hover:tw-border-red-300 tw-text-red-700 tw-text-xs tw-font-semibold tw-px-3 tw-py-2 tw-transition"
            (click)="onToggleDetails()"
            [attr.aria-expanded]="showDetails"
          >
            <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path [attr.d]="showDetails ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'"
                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ showDetails ? 'Hide details' : 'Show details' }}
          </button>

          @if (showRetry) {
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-white tw-border tw-border-red-200 hover:tw-border-red-300 tw-text-red-700 tw-text-xs tw-font-semibold tw-px-3 tw-py-2 tw-transition"
              (click)="onRetry()"
            >
              <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20 7h-7m7 0l-3-3m3 3-3 3M4 17h7m-7 0 3 3m-3-3 3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Retry
            </button>
          }
        </div>

        <!-- Collapsible details -->
        @if (showDetails) {
          <div class="tw-mt-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-white tw-p-3 tw-text-red-800">
            <dl class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-text-xs">
              <div class="tw-space-y-1">
                <dt class="tw-font-semibold tw-text-red-700">Message</dt>
                <dd class="tw-break-words">{{ normalized.message }}</dd>
              </div>
              <div class="tw-space-y-1">
                <dt class="tw-font-semibold tw-text-red-700">Reason</dt>
                <dd class="tw-break-words">{{ normalized.reason }}</dd>
              </div>
              <div class="tw-space-y-1">
                <dt class="tw-font-semibold tw-text-red-700">Status</dt>
                <dd>
                  @if (normalized.status) {
                    HTTP {{ normalized.status }}{{ normalized.statusText ? (' · ' + normalized.statusText) : '' }}
                  } @else {
                    N/A
                  }
                </dd>
              </div>
            </dl>

            <div class="tw-mt-3">
              <details class="tw-text-xs">
                <summary class="tw-cursor-pointer tw-font-semibold tw-text-red-700">Raw payload</summary>
                <pre class="tw-mt-2 tw-max-h-64 tw-overflow-auto tw-rounded-md tw-bg-red-50 tw-p-2 tw-text-[11px] tw-leading-snug tw-whitespace-pre-wrap tw-break-words">
{{ normalized.raw | json }}
                </pre>
              </details>
            </div>
          </div>
        }
      </div>
    </div>
  </section>
}
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorMessageComponent implements OnChanges, OnDestroy {
  /** Raw error from API (HttpErrorResponse | string | object | unknown) */
  @Input() error: unknown;

  /** Optional custom heading text */
  @Input() heading = 'Something went wrong';

  /** Show a “Retry” action button */
  @Input() showRetry = false;

  /** Emit when user clicks retry */
  @Output() readonly retry = new EventEmitter<void>();

  /** Exposed normalized error for the template */
  normalized: NormalizedError | null = null;

  /** UI state */
  showDetails = false;
  copied = false;

  private copyTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly norm = inject(ErrorNormalizerService);

  ngOnChanges(changes: SimpleChanges): void {
    if ('error' in changes) {
      this.normalized = this.error == null ? null : this.norm.extract(this.error);
      // Auto-collapse details on new error
      this.showDetails = false;
      this.copied = false;
      this.clearCopyTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearCopyTimer();
  }

  onToggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  async onCopy(): Promise<void> {
    if (!this.normalized) {
      return;
    }

    const payload = this.serializeForCopy(this.normalized);

    if (!navigator?.clipboard?.writeText) {
      // Graceful fallback for environments without Clipboard API
      try {
        // As a last resort, create a temporary textarea
        const textArea = document.createElement('textarea');
        textArea.value = payload;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showCopiedTick();
      }
      catch {
        window.alert('Could not copy to clipboard.');
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(payload);
      this.showCopiedTick();
    }
    catch {
      window.alert('Could not copy to clipboard.');
    }
  }

  onRetry(): void {
    this.retry.emit();
  }

  // --- helpers ---------------------------------------------------------------

  private serializeForCopy(normalizedError: NormalizedError): string {
    return JSON.stringify(
      {
        message: normalizedError.message,
        reason: normalizedError.reason,
        status: normalizedError.status,
        statusText: normalizedError.statusText,
        raw: normalizedError.raw,
      },
      null,
      2
    );
    }

  private showCopiedTick(): void {
    this.copied = true;
    this.clearCopyTimer();
    this.copyTimer = setTimeout(() => (this.copied = false), 1500);
  }

  private clearCopyTimer(): void {
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  }
}
